const SCRIPT_SUBTAG_PATTERN = /^[A-Za-z]{4}$/;

export const CITY_LANGUAGE_DISPLAY_NAMES = Object.freeze({
    // Chinese and East Asian languages
    'zh-Hant': '繁體中文',
    'zh-Hans': '簡體中文',
    'yue-Hant': '粵語',
    // Keep the backend's current Latn tags and support standard script aliases.
    'ja-Latn': '日本語',
    'ja-Jpan': '日本語',
    'ko-Latn': '한국어',
    'ko-Kore': '한국어',

    // Major European languages
    'en-Latn': 'English',
    'de-Latn': 'Deutsch',
    'pt-Latn': 'Português',
    'fr-Latn': 'Français',
    'es-Latn': 'Español',
    'it-Latn': 'Italiano',
    'nl-Latn': 'Nederlands',
    'pl-Latn': 'Polski',
    'ru-Cyrl': 'Русский',
    'uk-Cyrl': 'Українська',
    'tr-Latn': 'Türkçe',
    'sv-Latn': 'Svenska',
    'no-Latn': 'Norsk',
    'da-Latn': 'Dansk',
    'fi-Latn': 'Suomi',
    'cs-Latn': 'Čeština',
    'sk-Latn': 'Slovenčina',
    'hu-Latn': 'Magyar',
    'ro-Latn': 'Română',
    'bg-Cyrl': 'Български',
    'el-Grek': 'Ελληνικά',
    'hr-Latn': 'Hrvatski',
    'sl-Latn': 'Slovenščina',
    'sr-Cyrl': 'Српски',
    'sr-Latn': 'Srpski',
    'ca-Latn': 'Català',
    'lt-Latn': 'Lietuvių',
    'lv-Latn': 'Latviešu',
    'et-Latn': 'Eesti',

    // Southeast Asian and Pacific languages
    'id-Latn': 'Bahasa Indonesia',
    'ms-Latn': 'Bahasa Melayu',
    'vi-Latn': 'Tiếng Việt',
    'th-Thai': 'ไทย',
    'fil-Latn': 'Filipino',
    'km-Khmr': 'ភាសាខ្មែរ',
    'lo-Laoo': 'ພາສາລາວ',
    'my-Mymr': 'မြန်မာဘာသာ',

    // South Asian languages
    'hi-Deva': 'हिन्दी',
    'bn-Beng': 'বাংলা',
    'ur-Arab': 'اردو',
    'ta-Taml': 'தமிழ்',
    'te-Telu': 'తెలుగు',
    'mr-Deva': 'मराठी',
    'gu-Gujr': 'ગુજરાતી',
    'pa-Guru': 'ਪੰਜਾਬੀ',
    'ne-Deva': 'नेपाली',

    // Middle Eastern, Central Asian, and Caucasian languages
    'ar-Arab': 'العربية',
    'he-Hebr': 'עברית',
    'fa-Arab': 'فارسی',
    'az-Latn': 'Azərbaycanca',
    'kk-Cyrl': 'Қазақша',
    'uz-Latn': 'Oʻzbekcha',
    'ka-Geor': 'ქართული',
    'hy-Armn': 'Հայերեն',

    // Widely used African languages
    'sw-Latn': 'Kiswahili',
    'af-Latn': 'Afrikaans',
    'am-Ethi': 'አማርኛ',
    'zu-Latn': 'isiZulu',
    'xh-Latn': 'isiXhosa'
});

const LEGACY_LANGUAGE_DISPLAY_NAMES = {
    zh: '繁體中文',
    en: 'English',
    ko: '한국어',
    de: 'Deutsch',
    ja: '日本語',
    fr: 'Français',
    es: 'Español',
    pt: 'Português',
    ru: 'Русский',
    ar: 'العربية',
    hi: 'हिन्दी',
    th: 'ไทย',
    vi: 'Tiếng Việt',
    id: 'Bahasa Indonesia'
};

export function normalizeScriptCode(script) {
    const value = String(script || '').trim();
    if (!SCRIPT_SUBTAG_PATTERN.test(value)) return '';
    return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
}

export function parseLanguageTag(languageTag) {
    const parts = String(languageTag || '').trim().split('-').filter(Boolean);
    const lang = (parts[0] || '').toLowerCase();
    const script = normalizeScriptCode(parts.slice(1).find(part => SCRIPT_SUBTAG_PATTERN.test(part)) || '');

    return {
        lang,
        script,
        tag: lang && script ? `${lang}-${script}` : lang
    };
}

export function composeLanguageTag(lang, script = '') {
    const parsed = parseLanguageTag(lang);
    if (!parsed.lang) return '';
    const normalizedScript = parsed.script || normalizeScriptCode(script);
    return normalizedScript ? `${parsed.lang}-${normalizedScript}` : parsed.lang;
}

function getIntlLanguageDisplayName(lang) {
    if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
        return '';
    }

    try {
        const displayNames = new Intl.DisplayNames([lang], {
            type: 'language',
            fallback: 'none'
        });
        return displayNames.of(lang) || '';
    } catch (error) {
        return '';
    }
}

export function getCityLanguageDisplayName(languageTag) {
    const { lang, tag } = parseLanguageTag(languageTag);
    if (!lang) return '';

    return CITY_LANGUAGE_DISPLAY_NAMES[tag]
        || LEGACY_LANGUAGE_DISPLAY_NAMES[tag]
        || getIntlLanguageDisplayName(lang)
        || tag;
}

export function getLanguageTagDisplayName(languageTag) {
    return getCityLanguageDisplayName(languageTag);
}
